import {
  CUSTOMER_SORT_FIELDS,
  type CustomerBalanceFilter,
  type CustomerListQuery,
  customerBalanceFilterSchema,
  customerListQuerySchema,
} from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'

type CustomerSortField = (typeof CUSTOMER_SORT_FIELDS)[number]
export type CustomerSort = CustomerSortField | `-${CustomerSortField}`

export const CUSTOMER_PAGE_SIZES = [25, 50, 100] as const

export function isCustomerSort(value: string): value is CustomerSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (CUSTOMER_SORT_FIELDS as readonly string[]).includes(field)
}

export const customerViewSchema = customerListQuerySchema.shape.view.unwrap()
export type CustomerView = z.infer<typeof customerViewSchema>

/** The balance filters a list can apply ('any', the default, is never written to the URL). */
export const CUSTOMER_BALANCE_FILTERS = ['owes', 'overdue', 'credit'] as const satisfies readonly Exclude<
  CustomerBalanceFilter,
  'any'
>[]
export type CustomerBalanceView = (typeof CUSTOMER_BALANCE_FILTERS)[number]

/** Sorting or filtering by balance needs sales.read (the API answers 403 otherwise). */
export function isBalanceSort(sort: string | undefined): boolean {
  return sort === 'balance' || sort === '-balance'
}

/**
 * The customers list lives in the URL (shareable, back-button safe). Every key is optional and an
 * invalid value silently falls back to the default instead of breaking the page; defaults are
 * applied by `toListQuery` so they never clutter the URL.
 */
export const customerSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is (typeof CUSTOMER_PAGE_SIZES)[number] =>
      (CUSTOMER_PAGE_SIZES as readonly number[]).includes(n),
    )
    .optional()
    .catch(undefined),
  sort: z.string().refine(isCustomerSort).optional().catch(undefined),
  // A hand-written `?q=99112045` arrives as a number (the router parses JSON): keep it as text.
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  view: customerViewSchema.optional().catch(undefined),
  groupId: z.uuid().optional().catch(undefined),
  /** Owes us / Overdue / In credit (needs sales.read). */
  balance: customerBalanceFilterSchema.exclude(['any']).optional().catch(undefined),
})

export type CustomerSearch = z.infer<typeof customerSearchSchema>

/** The list request with every default resolved (the contract's query type, with concrete paging). */
export type CustomerListParams = Omit<CustomerListQuery, 'page' | 'pageSize' | 'view' | 'balance'> & {
  page: number
  pageSize: number
  view: CustomerView
  balance?: CustomerBalanceView | undefined
}

export function toListQuery(search: CustomerSearch): CustomerListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? CUSTOMER_PAGE_SIZES[0],
    sort: search.sort,
    q: search.q || undefined,
    view: search.view ?? 'current',
    groupId: search.groupId,
    balance: search.balance,
  }
}

// ── the customer profile ─────────────────────────────────────────────────────

export const PROFILE_TABS = [
  'overview',
  'invoices',
  'receipts',
  'credit-notes',
  'quotations',
  'statement',
  'cheques',
  'tasks',
  'service',
  'contracts',
  'projects',
  'loyalty',
  'attachments',
] as const
export type ProfileTabId = (typeof PROFILE_TABS)[number]
export type DocumentTabId = Exclude<
  ProfileTabId,
  | 'overview'
  | 'cheques'
  | 'statement'
  | 'tasks'
  | 'service'
  | 'contracts'
  | 'projects'
  | 'loyalty'
  | 'attachments'
>

/** Each document tab's views (the first is the default and never written to the URL). */
export const DOCUMENT_VIEWS = {
  invoices: ['all', 'open', 'overdue', 'draft', 'void'],
  receipts: ['all', 'credit', 'void'],
  'credit-notes': ['all', 'credit', 'draft', 'void'],
  quotations: ['all', 'pending', 'approved', 'converted', 'rejected'],
} as const satisfies Record<DocumentTabId, readonly string[]>
export type DocumentView<T extends DocumentTabId> = (typeof DOCUMENT_VIEWS)[T][number]

const profileDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)

/**
 * The profile keeps its state in the URL: the open tab, that tab's view, type filter, page and sort
 * (reset when the tab changes), and the statement period. `print` asks the statement to print once
 * it has loaded (the More menu's "Print statement").
 */
export const customerProfileSearchSchema = z.object({
  tab: z.enum(PROFILE_TABS).optional().catch(undefined),
  view: z.string().max(20).optional().catch(undefined),
  kind: z.enum(['INVOICE', 'DEBIT_NOTE']).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  sort: z.string().max(40).optional().catch(undefined),
  from: profileDate,
  to: profileDate,
  print: z.boolean().optional().catch(undefined),
})
export type CustomerProfileSearch = z.infer<typeof customerProfileSearchSchema>

export function documentView<T extends DocumentTabId>(tab: T, view: string | undefined): DocumentView<T> {
  const views: readonly string[] = DOCUMENT_VIEWS[tab]
  return (view && views.includes(view) ? view : views[0]) as DocumentView<T>
}
